import { deepSignal } from 'deepsignal';
import CustomizeSection from '@components/overlays/shared/customize-section.jsx';
import ColorPicker from '@components/pickers/color-picker/color.picker.jsx';
import Picker from '@components/input/picker.input.jsx';
import NumberInput from '@components/input/number.input.jsx';
import LinkSetup from '@components/overlays/social/link-setup/link-setup.jsx';
import { isYes } from '@util/common.js';
import { CHARITY } from '@util/const.js';

import {
  DonorListOverlay,
  DonorListOverlayBackgroundColors,
} from '@playlive/overlay-core/overlays/donor-list';
import { Color } from '@playlive/overlay-core/colors';
import ScaledOverlayWrapper from '@components/overlays/wrapper/scaled-overlay-wrapper.jsx';
import YesNoButtonInput from '@components/input/yes-no-button.input.jsx';
import useCommon from '@hooks/useCommon.js';
import ColorPickerLabel from '@components/pickers/color-picker/color-picker-label.jsx';
import HorizontalCustomizeWrapper from '@components/overlays/wrapper/horizonal-customize-wrapper.jsx';

const config = deepSignal({
  charityType: CHARITY.TILTIFY,
  donorListBackgroundColor: Color.DEFAULT_RED,
  donorListDisplayIncentives: 'yes',
  donorListOrientation: 'vertical',
  donorListAlign: 'left',
  donorListAmount: 5,
});
/** @typedef {import('@playlive/overlay-core').DonorListItem} DonorListItem **/
export default function DonorListOverlayPage() {
  const { overlayInfo, overlayURL, setConfigState } = useCommon({
    config,
    forceTiltify: true,
  });

  const showAlignmentPicker = config.donorListOrientation === 'vertical';

  const orientations = [
    { value: 'vertical', name: 'Vertical' },
    { value: 'horizontal', name: 'Horizontal' },
  ];

  const alignments = [
    { value: 'left', name: 'Left' },
    { value: 'right', name: 'Right' },
  ];

  /**
   *
   * @type {Array<DonorListItem>}
   */
  const demoDonors = [
    {
      rewardName: '10 Push-ups',
      amount: 250,
      id: '1',
      name: 'Stacey',
      optionName: 'Pineapple',
    },
    {
      amount: 200,
      name: 'Mike',
      id: '2',
    },
    {
      amount: 10,
      name: 'Steve',
      id: '3',
      causeName: 'New Toy for Hospital Play Areas',
    },
    {
      amount: 250,
      name: 'Paul',
      id: '4',
    },
  ];

  function filterColors(color) {
    return DonorListOverlayBackgroundColors.find((c) => c === color);
  }

  return (
    <div className="w-full overflow-x-hidden">
      <div className="flex flex-col xl:flex-row! overflow-x-hidden">
        <div className="example_overlay select-none overflow-x-hidden w-full">
          <ScaledOverlayWrapper allowScaling={false} addPadding={false}>
            <DonorListOverlay
              backgroundColor={config.donorListBackgroundColor}
              alignment={config.donorListAlign}
              displayIncentives={isYes(config.donorListDisplayIncentives)}
              showDonors={config.donorListAmount}
              orientation={config.donorListOrientation}
              donors={demoDonors}
            />
          </ScaledOverlayWrapper>
        </div>

        <HorizontalCustomizeWrapper className="flex flex-col mb-0!">
          <CustomizeSection title="Appearance">
            <ColorPickerLabel color={config.donorListBackgroundColor} />

            <ColorPicker
              fieldName="donorListBackgroundColor"
              currentColor={config.donorListBackgroundColor}
              filterFunction={filterColors}
              onChange={setConfigState}
            />

            <NumberInput
              name="donorListAmount"
              value={Number(config.donorListAmount)}
              placeholder="5"
              onChange={setConfigState}
              isCurrency={false}
              maxLength={4}
              suffix="donors"
              labelText="Donor Count"
              description="Amount of donors to display"
            />

            <YesNoButtonInput
              mode="checkbox"
              onChange={setConfigState}
              currentValue={config.donorListDisplayIncentives}
              name="donorListDisplayIncentives"
              label="Display claimed rewards and polls"
            />
          </CustomizeSection>

          <CustomizeSection title="Position" className="flex-grow">
            <Picker
              items={orientations}
              fieldName="donorListOrientation"
              onChange={setConfigState}
              currentValue={config.donorListOrientation}
              labelText="Orientation"
            />

            <Picker
              disabled={!showAlignmentPicker}
              items={alignments}
              fieldName="donorListAlign"
              onChange={setConfigState}
              currentValue={config.donorListAlign}
              labelText="Alignment"
              description="Only available in the vertical orientation"
            />
          </CustomizeSection>

          {/* Charity Section Placeholder */}
        </HorizontalCustomizeWrapper>
      </div>

      <LinkSetup
        constrainWidth={false}
        charityType={CHARITY.TILTIFY}
        obsDimensions={overlayInfo.obsDimensions}
        orientation={config.donorListOrientation}
        overlayName={overlayInfo.name}
        overlayURL={overlayURL}
        finalStepSlotPosition="middle"
        showCopyDemoModeLinkButton
      />
    </div>
  );
}
